// 사장님이 기사 담당 동네·학교를 정해 주는 화면(대표 결정 Q23) — 맡기기는 서버 assignDongClaim, 빼기는 담당 기록 직접 삭제(규칙: 관리자 허용)
// 기사가 직접 고르는 화면(기사 화면 「이번달 담당」)은 그대로 둔다. 한 동네를 기사 여럿이 함께 맡을 수 있다(기사 화면과 같다).
import { useEffect, useMemo, useState } from "react";
import { deleteDoc, doc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import clsx from "clsx";
import { db, functions } from "@/lib/firebase";
import { useStudents } from "@/hooks/useStudents";
import { useDongClaims } from "@/hooks/useDongClaims";
import { fmtYearMonth } from "@/lib/format";
import { claimKey, unclaimedStudents } from "@/lib/claims";
import { normalizeSchool } from "@/lib/school";
import { plainError } from "@/lib/plainError";
import { useOrgStore } from "@/store/org";
import type { Student } from "@/types";

interface Person { uid: string; name: string; role: "admin" | "driver" }
interface Row { key: string; scope: "dong" | "school"; gu: string; dong: string; school: string; label: string; students: Student[] }

export default function AssignDongs() {
  const now = new Date();
  const months = [fmtYearMonth(now), fmtYearMonth(new Date(now.getFullYear(), now.getMonth() + 1, 1))];
  const [ym, setYm] = useState(months[0]);
  const [groupBy, setGroupBy] = useState<"dong" | "school">("dong");
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [pick, setPick] = useState<Record<string, string>>({}); // 줄 → 고른 기사 uid
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [people, setPeople] = useState<Person[] | null>(null);
  const { students, loading } = useStudents();
  const { claims } = useDongClaims(ym);
  const ownerUid = useOrgStore((s) => s.org?.createdBy) || ""; // 대표(업체를 만든 계정)만 「사장님」, 다른 관리자는 「관리자」

  useEffect(() => {
    httpsCallable(functions, "listUsers")()
      .then((res) => {
        const users = (res.data as { users: Array<{ uid: string; displayName: string; email: string; role: string | null }> }).users;
        setPeople(users.filter((u) => u.role === "driver" || u.role === "admin")
          .map((u) => ({ uid: u.uid, name: u.displayName || u.email || u.uid.slice(0, 6), role: u.role as Person["role"] }))
          .sort((a, b) => (a.role === b.role ? a.name.localeCompare(b.name) : a.role === "driver" ? -1 : 1)));
      })
      .catch((e) => { setPeople([]); setMsg({ kind: "err", text: `기사 목록을 불러오지 못했습니다 — ${plainError(e)}` }); });
  }, []);

  // 직배송 학생만(택배·배송 대상 아님 제외) — 담당이 필요한 학생
  const direct = useMemo(() => students.filter((s) => (!s.status || s.status === "active") && !s.courierFlag), [students]);
  const open = useMemo(() => unclaimedStudents(direct, claims), [direct, claims]);
  const noDong = open.filter((s) => !s.gu || !s.dong).length;

  const rows = useMemo(() => {
    const m = new Map<string, Row>();
    for (const s of direct) {
      if (groupBy === "dong" ? !s.gu || !s.dong : !s.school) continue;
      const school = normalizeSchool(s.school);
      const key = groupBy === "dong" ? `${s.gu}|${s.dong}` : `S::${school}`;
      if (!m.has(key)) {
        m.set(key, groupBy === "dong"
          ? { key, scope: "dong", gu: s.gu, dong: s.dong, school: "", label: `${s.gu} ${s.dong}`, students: [] }
          : { key, scope: "school", gu: "", dong: "", school, label: school, students: [] });
      }
      m.get(key)!.students.push(s);
    }
    // 학생이 0명이 된 곳(전출·택배·이사)의 담당도 보여야 뺄 수 있다(코덱스 검토)
    for (const c of claims) {
      const key = claimKey(c);
      if (m.has(key) || (c.scope === "school") !== (groupBy === "school")) continue;
      m.set(key, c.scope === "school"
        ? { key, scope: "school", gu: "", dong: "", school: c.school || "", label: c.school || "", students: [] }
        : { key, scope: "dong", gu: c.gu, dong: c.dong, school: "", label: `${c.gu} ${c.dong}`, students: [] });
    }
    return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [direct, groupBy, claims]);

  const openIds = useMemo(() => new Set(open.map((s) => s.id)), [open]);
  const shown = rows.filter((r) => !onlyOpen || r.students.some((s) => openIds.has(s.id)));

  const assign = async (r: Row) => {
    const uid = pick[r.key];
    if (!uid) { setMsg({ kind: "err", text: "맡을 기사님을 먼저 고르세요." }); return; }
    setBusy(r.key);
    setMsg(null);
    try {
      const res = await httpsCallable(functions, "assignDongClaim")({
        yearMonth: ym, scope: r.scope, gu: r.gu, dong: r.dong, school: r.school, driverUid: uid, studentCount: r.students.length,
      });
      const who = people?.find((p) => p.uid === uid)?.name || "기사";
      // 이름·동네 뒤 조사(을/를)는 받침에 따라 달라 조사 없이 쓴다
      setMsg({ kind: "ok", text: (res.data as { already?: boolean }).already ? `${r.label} 담당은 이미 ${who} 님입니다.` : `${r.label} 담당을 ${who} 님으로 정했습니다(${ym}).` });
    } catch (e) {
      setMsg({ kind: "err", text: `맡기지 못했습니다 — ${plainError(e)}` });
    } finally {
      setBusy("");
    }
  };

  const unassign = async (claimId: string, label: string, name: string) => {
    if (!window.confirm(`${name} 기사님의 ${label} 담당을 뺄까요?\n그 기사님 경로에서 이 동네가 빠집니다.`)) return;
    setBusy(claimId);
    setMsg(null);
    try {
      await deleteDoc(doc(db, "dongClaims", claimId));
      setMsg({ kind: "ok", text: `${name} 기사님의 ${label} 담당을 뺐습니다.` });
    } catch (e) {
      setMsg({ kind: "err", text: `빼지 못했습니다 — ${plainError(e)}` });
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">📍 담당 동네 정하기</h1>
        <select className="input max-w-[160px]" value={ym} onChange={(e) => setYm(e.target.value)} aria-label="담당 달">
          {months.map((m, i) => <option key={m} value={m}>{m}{i === 0 ? " (이번 달)" : " (다음 달)"}</option>)}
        </select>
      </div>
      <p className="text-sm text-slate-500">기사님별로 그 달 맡을 동네나 학교를 정해 줍니다. 기사님이 직접 고른 담당도 여기에 함께 보입니다. 한 동네를 두 분이 같이 맡을 수도 있습니다.</p>

      <div className={clsx("card p-4 border", open.length ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50")}>
        <div className={clsx("font-bold", open.length ? "text-rose-700" : "text-emerald-700")}>
          {loading ? "학생 명단 불러오는 중…" : open.length ? `${ym} 아무도 안 맡은 학생 ${open.length}명` : `${ym} 모든 직배송 학생에 담당 기사가 있습니다`}
        </div>
        {open.length > 0 && <div className="text-xs text-rose-600 mt-1">이 학생들은 어느 기사님 경로에도 나오지 않아 배송에서 빠집니다. 아래에서 맡겨 주세요.</div>}
        {noDong > 0 && groupBy === "dong" && <div className="text-xs text-rose-600 mt-1">그중 {noDong}명은 동 이름이 없어 동별로 맡길 수 없습니다 — 「학교별」로 맡기거나 「주소 확인 대기」에서 주소를 고쳐 주세요.</div>}
      </div>

      {msg && <div className={clsx("card p-3 text-sm", msg.kind === "ok" ? "text-emerald-700" : "text-rose-700")} role="status">{msg.text}</div>}

      <div className="flex items-center gap-3 flex-wrap text-sm">
        <div className="inline-flex rounded-lg border bg-white overflow-hidden">
          <button className={clsx("px-3 min-h-[40px]", groupBy === "dong" ? "bg-milk-500 text-white" : "text-slate-600")} onClick={() => setGroupBy("dong")}>동별</button>
          <button className={clsx("px-3 min-h-[40px]", groupBy === "school" ? "bg-milk-500 text-white" : "text-slate-600")} onClick={() => setGroupBy("school")}>학교별</button>
        </div>
        <label className="inline-flex items-center gap-2 min-h-[40px]">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> 안 맡은 학생이 있는 곳만
        </label>
        {people && people.length === 0 && <span className="text-rose-600">기사님이 없습니다 — 「사용자 권한」에서 초대해 주세요.</span>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {shown.map((r) => {
          const mine = claims.filter((c) => claimKey(c) === r.key);
          const left = r.students.filter((s) => openIds.has(s.id)).length;
          return (
            <div key={r.key} className="card p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold">{r.label}</div>
                <div className="text-sm text-slate-500">{r.students.length}명{left > 0 && <span className="text-rose-600 font-semibold"> · 안 맡음 {left}</span>}</div>
              </div>
              <div className="flex flex-wrap gap-1">
                {mine.length === 0 && <span className="text-xs text-slate-400">{r.scope === "school" ? "학교 단위로 맡은 기사 없음(동별 담당은 「동별」에서 봅니다)" : "맡은 기사 없음"}</span>}
                {mine.map((c) => (
                  <button key={c.id} className="chip bg-milk-100 text-milk-800 text-xs min-h-[32px] disabled:opacity-40" disabled={busy === c.id}
                    onClick={() => unassign(c.id, r.label, c.driverName || "기사")} title="눌러서 이 담당 빼기">
                    {c.driverName || c.driverUid.slice(0, 6)} ✕
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <select className="input flex-1 text-sm" value={pick[r.key] || ""} onChange={(e) => setPick((p) => ({ ...p, [r.key]: e.target.value }))} aria-label={`${r.label} 맡을 기사`}>
                  <option value="">기사님 고르기</option>
                  {(people || []).map((p) => <option key={p.uid} value={p.uid}>{p.name}{p.role === "admin" ? (p.uid === ownerUid ? " (사장님)" : " (관리자)") : ""}</option>)}
                </select>
                <button className="btn-primary min-h-[44px] disabled:opacity-40" disabled={busy === r.key || !pick[r.key]} onClick={() => assign(r)}>
                  {busy === r.key ? "맡기는 중…" : "맡기기"}
                </button>
              </div>
            </div>
          );
        })}
        {!loading && shown.length === 0 && (
          <div className="col-span-full card p-6 text-center text-sm text-slate-500">
            {onlyOpen ? "안 맡은 학생이 있는 곳이 없습니다. 「안 맡은 학생이 있는 곳만」을 끄면 전체가 보입니다." : "직배송 학생이 없습니다."}
          </div>
        )}
      </div>
    </div>
  );
}
